/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { LegView } from '@sequence/models/view/leg.view';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { AlertService } from '@shared/services/alert/alert.service';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';
import { StandbyListResponse } from './models/standby-list.response';
import { StandbyListView } from './models/standby-list.view';
import { StandbyListService } from './standby-list.service';

@Injectable({
  providedIn: 'root',
})
export class StandbyListStore {
  private readonly _listFilter = new BehaviorSubject<'all' | 'stby' | 'nonrev'>(
    'nonrev'
  );

  listFilter$ = this._listFilter.asObservable();

  constructor(
    private standbyListService: StandbyListService,
    private alertService: AlertService
  ) {}

  /** Filter the standby list based on the show revenue standbys option. */
  getFilteredStandbyList(
    legView: LegView,
    isRefresh = false
  ): Observable<RequestState<StandbyListView>> {
    return combineLatest([
      this.getStandbyList(legView, isRefresh),
      this.listFilter$,
    ]).pipe(
      map(([standbyListState, listFilter]) => {
        if (standbyListState.hasError) {
          this.showErrorAlert();
        }

        if (!standbyListState.response) {
          return standbyListState.into<StandbyListView>();
        }

        const standbyListView = new StandbyListView(
          standbyListState.response.raw || '',
          listFilter,
          standbyListState.response.timestamp
        );

        return standbyListState.into<StandbyListView>(standbyListView);
      })
    );
  }

  setListFilter(newValue: 'all' | 'stby' | 'nonrev'): void {
    this._listFilter.next(newValue);
  }

  getStandbyList(
    legView: LegView,
    isRefresh = false
  ): Observable<RequestState<StandbyListResponse>> {
    const request = legView.getStandbyListRequest();

    if (!request) {
      return of(
        new RequestState<StandbyListResponse>(
          RequestStatus.IsCompleteNoResponse
        )
      );
    }

    return this.standbyListService.getStandbyList(request, isRefresh);
  }

  private showErrorAlert(): void {
    this.alertService.setAlert({
      type: 'error',
      message: 'Unable to get standby list data.',
    });
  }
}
